/**
 * Import Progress Indicator — single active-import row.
 *
 * A dumb presentational component for ONE queued or running import: venue
 * tile, state, live elapsed time, progress bar and records counter. Extracted
 * from ImportProgressIndicator.tsx so that file stays focused on orchestration.
 */

import { useTranslation } from 'react-i18next'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'
import { translateSyncStageLabel } from '@/platform/sync/syncStageLabels'
import { userConfigService } from '@/shared/services/user-config.service'

import {
  STATE_FILL_CLASS,
  STATE_TEXT_CLASS,
  VenueTile,
  formatElapsed,
  type ImportRowState,
} from './ImportRowParts'

export interface ActiveImport {
  jobId: string
  sourceName: string
  type: string
  /** null = the total is unknown (a sync learns it only at its last page): no percentage. */
  progress: number | null
  recordsProcessed: number
  totalRecords?: number
  currentStage?: string
  waitingFor?: string | null
  waitingUntil?: string | null
  startedAt: Date
  status: 'processing' | 'pending' | 'paused'
}

interface ImportItemProps {
  import_: ActiveImport
  formatRelativeTime: (date: Date) => string
  /** Epoch ms of the panel's shared one-second clock (drives the elapsed time). */
  now?: number
}

const rowStateOf = (import_: ActiveImport): ImportRowState => {
  if (import_.currentStage === 'waiting_provider') return 'waiting'
  return import_.status === 'processing' ? 'running' : 'queued'
}

export const ImportItem = ({ import_, formatRelativeTime, now }: ImportItemProps) => {
  const { t } = useTranslation('imports')
  // A CSV import always carries `sourceId: 'unknown'`, so the monitoring
  // service fills `sourceName` with its ENGLISH sentinel 'Unknown Source' —
  // rendering it raw titled every Spanish import ticket in English.
  // `resolveSyncSourceLabel` is the ONE resolver for that sentinel (already
  // wired into the two admin surfaces); its key `monitoring.unknownSource`
  // lives in the COMMON catalog, not in `imports`, hence the second hook.
  const { t: tCommon } = useTranslation('common')
  const sourceLabel = resolveSyncSourceLabel(import_.sourceName, tCommon)

  const formatProgress = () => {
    if (import_.totalRecords) {
      return `${userConfigService.formatNumberWithFixedDecimals(import_.recordsProcessed, 0)} / ${userConfigService.formatNumberWithFixedDecimals(import_.totalRecords, 0)}`
    }
    // i18next plural (_one/_other) con el número YA formateado en `formatted`:
    // concatenarlo a un sustantivo fijo imprimía «1 registros» — cazado en vivo
    // importando un CSV de una sola fila (2026-08-09).
    return t('progressIndicator.records', {
      count: import_.recordsProcessed,
      formatted: userConfigService.formatNumberWithFixedDecimals(import_.recordsProcessed, 0),
    })
  }

  // A percentage only when there is a total; a provider's rate limit is a wait
  // (amber clock), never a spinner stuck at 0 % in red.
  const progress = typeof import_.progress === 'number' ? import_.progress : null
  const state = rowStateOf(import_)
  const startedMs = import_.startedAt.getTime()
  const hasStart = !Number.isNaN(startedMs)
  const elapsed = hasStart && now !== undefined ? formatElapsed(now - startedMs) : null

  const stageLabel = import_.currentStage
    ? // Machine stage slugs ('connection_established', …) render translated;
      // free-form progress messages pass through.
      translateSyncStageLabel(import_.currentStage, t, {
        provider: import_.waitingFor ?? '',
        time: import_.waitingUntil
          ? new Date(import_.waitingUntil).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
      })
    : null

  return (
    <div
      className="flex gap-3 rounded-lg bg-crypto-dark/40 px-3 py-2.5"
      data-testid={`active-import-item-${import_.jobId}`}
      data-state={state}
    >
      <VenueTile label={sourceLabel} type={import_.type} state={state} />

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-crypto-text" title={sourceLabel}>
            {sourceLabel}
          </span>
          {elapsed !== null ? (
            <time
              className="flex-shrink-0 text-xs tabular-nums text-crypto-muted"
              dateTime={`PT${Math.max(0, Math.floor(((now ?? startedMs) - startedMs) / 1000))}S`}
              title={`${t('progressIndicator.started', 'Started')} ${formatRelativeTime(import_.startedAt)}`}
              aria-label={t('progressIndicator.elapsed', { time: elapsed, defaultValue: 'Elapsed {{time}}' })}
            >
              {elapsed}
            </time>
          ) : (
            hasStart && (
              <span className="flex-shrink-0 text-xs text-crypto-muted">
                {formatRelativeTime(import_.startedAt)}
              </span>
            )
          )}
        </div>

        <div
          className="h-1.5 overflow-hidden rounded-full bg-crypto-border/60"
          role="progressbar"
          aria-label={t('progressIndicator.progressOf', { name: sourceLabel, defaultValue: 'Progress of {{name}}' })}
          aria-valuemin={0}
          aria-valuemax={100}
          {...(progress !== null ? { 'aria-valuenow': progress } : {})}
        >
          {progress !== null ? (
            <div
              className={`h-full rounded-full ${STATE_FILL_CLASS[state]} motion-safe:transition-all motion-safe:duration-500`}
              style={{ width: `${progress}%` }}
            />
          ) : (
            // indeterminate: it is working (or queued/waiting), how much is left is not known
            <div
              className={`h-full w-1/3 rounded-full ${STATE_FILL_CLASS[state]} ${state === 'running' ? 'motion-safe:animate-pulse' : 'opacity-70'}`}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 text-xs">
          <span className={`truncate ${STATE_TEXT_CLASS[state]}`}>
            {stageLabel ?? t(`progressIndicator.state.${state}`, stateDefault[state])}
          </span>
          <span className="flex flex-shrink-0 items-center gap-2 tabular-nums text-crypto-muted">
            {/* A queued job has read nothing yet: «0 registros» would be noise. */}
            {(state !== 'queued' || import_.recordsProcessed > 0 || import_.totalRecords) && (
              <span>{formatProgress()}</span>
            )}
            {progress !== null && <span className="font-medium text-crypto-text">{progress}%</span>}
          </span>
        </div>
      </div>
    </div>
  )
}

const stateDefault: Record<ImportRowState, string> = {
  queued: 'Queued',
  running: 'Importing',
  waiting: 'Waiting for the provider',
  done: 'Completed',
  failed: 'Failed',
}
